import React, { useState } from 'react';
import { DayOffer } from '../types';
import { format, parseISO } from 'date-fns';
import {
  Sun,
  CloudRain,
  Plus,
  Minus,

  Lock,
  CheckCircle2,
  AlertCircle,
  BatteryMedium,
} from 'lucide-react';
import {
  BLOCK_PRODUCTION_CEILING,
  DayPoolBudget,
  HOURLY_LIMITS,
  canStep,
  hybridBlockMax,
  katoBlockSum,
  stepValue,
} from '../utils/naerasBid';
import ConfirmationDialog from '@/lib/components/ui/ConfirmationDialog';

interface OfferAdjusterProps {
  offer: DayOffer;
  allSelectedOffers?: DayOffer[];
  /** The pool this day draws on — shared across days, see `dayPoolBudget`. */
  poolBudget: DayPoolBudget;
  onOfferChange: (updated: DayOffer) => void;
}

export const OfferAdjuster: React.FC<OfferAdjusterProps> = ({
  offer,
  allSelectedOffers = [offer],
  poolBudget,
  onOfferChange,
}) => {
  // The season is decided by the server. This only previews the other layout;
  // there is no endpoint that changes a bid's mode.
  const [hasConfirmedEdit, setHasConfirmedEdit] = useState(false);

  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const executeWithConfirm = (action: () => void) => {
    if (isLocked && !hasConfirmedEdit) {
      setPendingAction(() => action);
      setShowConfirm(true);
    } else {
      action();
    }
  };

  const isWinter = offer.mode === 'winter_mode';
  const isLocked = offer.status === 'final';
  // Hourly series are only adjustable in the wet season, and never once final.
  const canEditHydro = true; // User requested to always be able to edit this

  const limits = { ...offer.limits, ...HOURLY_LIMITS };

  // 12-hour sums of ΜΥΗΣ Κάτω Προεσπέρας in MWh (hourly MW × 1h = MWh)
  const katoSum1st12h = katoBlockSum(offer, 0);
  const katoSum2nd12h = katoBlockSum(offer, 1);

  // Grand total hybrid energy across all selected days
  const otherOffers = allSelectedOffers.filter((o) => o.date !== offer.date);
  const sumOtherOffersHybrid = otherOffers.reduce(
    (sum, o) => sum + o.hybrid1st12h + o.hybrid2nd12h,
    0
  );
  const currentDayHybridTotal = Number((offer.hybrid1st12h + offer.hybrid2nd12h).toFixed(1));
  const grandTotalHybridMwh = Number((sumOtherOffersHybrid + currentDayHybridTotal).toFixed(1));

  // Past tomorrow the pool is no ceiling — pumping may refill it before then
  const poolCeiling = poolBudget.pumpable ? null : poolBudget.remaining;

  // Live block ceilings, mirroring the server's own formula
  const maxAllowedInjection1st = Number(Math.max(0, hybridBlockMax(offer, 0, poolCeiling)).toFixed(1));
  const maxAllowedInjection2nd = Number(Math.max(0, hybridBlockMax(offer, 1, poolCeiling)).toFixed(1));

  // Check 1: the block's Kato total plus its hybrid stays under 3.1 MW × 12h
  const totalInjection1st12h = Number((katoSum1st12h + offer.hybrid1st12h).toFixed(1));
  const isInjection1stValid = totalInjection1st12h <= BLOCK_PRODUCTION_CEILING;

  const totalInjection2nd12h = Number((katoSum2nd12h + offer.hybrid2nd12h).toFixed(1));
  const isInjection2ndValid = totalInjection2nd12h <= BLOCK_PRODUCTION_CEILING;

  // Check 2: both blocks of this day together stay inside what is left of the
  // pool. Past tomorrow going over only means the day relies on pumping.
  const isHybridPoolValid = currentDayHybridTotal <= Number(poolBudget.remaining.toFixed(2));
  const poolTone = isHybridPoolValid
    ? 'text-emerald-600'
    : poolBudget.pumpable
    ? 'text-amber-600'
    : 'text-rose-600';

  // Past tomorrow going over the pool was always allowed, so only the checks
  // that used to cap the +/− buttons raise the warning — plus whatever the
  // server flagged on the saved draft
  const isHybridOverLimit =
    !isInjection1stValid ||
    !isInjection2ndValid ||
    (!isHybridPoolValid && !poolBudget.pumpable) ||
    offer.hybridWarnings.length > 0;

  /** Steps one hour of one series along the grid anchored at the NAERAS value. */
  const updateHourlyValue = (hour: number, field: 'mysAno' | 'mysKato', delta: number) => {
    if (!canEditHydro) return;

    executeWithConfirm(() => {
      const limit = field === 'mysAno' ? limits.upper : limits.lower;
      const computedSeries = field === 'mysAno' ? offer.computed.mysAno : offer.computed.mysKato;

      const updatedHourly = offer.hourly.map((h) => {
        if (h.hour !== hour) return h;

        return {
          ...h,
          [field]: stepValue(h[field], Number(computedSeries?.[hour] ?? 0), delta, limit),
        };
      });

      onOfferChange({ ...offer, hourly: updatedHourly, hybridWarnings: [] });
    });
  };

  /**
   * The hybrid blocks have no ceiling: going past the production or pool check
   * is allowed and only raises a warning. The floor stays at 0.
   */
  const hybridLimit = {
    step: limits.hybrid.step,
    min: 0,
    max: Number.POSITIVE_INFINITY,
  };

  /** Steps a hybrid block along the grid anchored at the NAERAS value. */
  const updateHybridBlock = (block: 1 | 2, delta: number) => {
    executeWithConfirm(() => {
      const index = (block - 1) as 0 | 1;
      const current = block === 1 ? offer.hybrid1st12h : offer.hybrid2nd12h;

      const next = stepValue(current, Number(offer.computed.hybrid?.[index] ?? current), delta, hybridLimit);
      if (next === current) return;

      // The server's warnings describe the saved draft; until the PATCH
      // answers with fresh ones, the local checks speak for this edit
      onOfferChange(
        block === 1
          ? { ...offer, hybrid1st12h: next, hybridWarnings: [] }
          : { ...offer, hybrid2nd12h: next, hybridWarnings: [] }
      );
    });
  };

  const canStepHybrid = (block: 0 | 1, delta: number) => {

    const current = block === 0 ? offer.hybrid1st12h : offer.hybrid2nd12h;

    return canStep(current, Number(offer.computed.hybrid?.[block] ?? current), delta, hybridLimit);
  };

  /** Check 1, per block: Kato plus hybrid within what the unit can produce. */
  const renderProductionCheck = (block: 0 | 1) => {
    const katoSum = block === 0 ? katoSum1st12h : katoSum2nd12h;
    const hybrid = block === 0 ? offer.hybrid1st12h : offer.hybrid2nd12h;
    const total = block === 0 ? totalInjection1st12h : totalInjection2nd12h;
    const isValid = block === 0 ? isInjection1stValid : isInjection2ndValid;

    return (
      <div className="flex items-start justify-between gap-2">
        <div className="text-gray-600">
          <div className="font-semibold text-gray-700">1. Έλεγχος Δυνατότητας Παραγωγής 12ώρου:</div>
          <div className="text-[11px] text-gray-500">
            ΜΥΗΣ Κάτω Π. ({katoSum.toFixed(1)}) + {block + 1}ο 12ωρο ({hybrid.toFixed(2)}) &le; 3.1 MW
            &times; 12h
          </div>
        </div>
        <div className="flex items-center gap-1 font-bold">
          <span className={isValid ? 'text-emerald-600' : 'text-rose-600'}>
            {total} / {BLOCK_PRODUCTION_CEILING} MWh
          </span>
          {isValid ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          ) : (
            <AlertCircle className="h-4 w-4 text-rose-600" />
          )}
        </div>
      </div>
    );
  };

  /** Check 2, per day: both blocks within what is left of the pool. */
  const renderPoolCheck = () => (
    <div className="flex items-start justify-between gap-2 border-t border-gray-100 pt-1.5">
      <div className="text-gray-600">
        <div className="font-semibold text-gray-700">2. Εκτιμώμενος έλεγχος Αποθέματος Υβριδικής:</div>
        <div className="text-[11px] text-gray-500">
          {poolBudget.pumpable
            ? 'Άθροισμα των 2 δωδεκαώρων ≤ απόθεμα μείον τις προηγούμενες ημέρες'
            : 'Άθροισμα των 2 δωδεκαώρων της ημέρας ≤ απόθεμα'}
        </div>
      </div>
      <div className="flex items-center gap-1 whitespace-nowrap font-bold">
        <span className={poolTone}>
          {currentDayHybridTotal} / {poolBudget.remaining.toFixed(2)} MWh
          {poolBudget.pumpable && ' + pump'}
        </span>
        {isHybridPoolValid ? (
          <CheckCircle2 className={`h-4 w-4 ${poolTone}`} />
        ) : (
          <AlertCircle className={`h-4 w-4 ${poolTone}`} />
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Top Banner & Season Selector */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-gray-100 bg-white p-6 shadow-xl shadow-gray-900/5">
        <div className="flex items-center gap-3">
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-2xl ${
              isWinter ? 'bg-blue-600 text-white' : 'bg-amber-500 text-white'
            } shadow-lg`}
          >
            {isWinter ? <CloudRain className="h-6 w-6" /> : <Sun className="h-6 w-6" />}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-xl font-bold text-gray-900">
                Επεξεργασία Προσφοράς – {offer.dayLabel}
              </h3>
              <span
                className={`rounded-full px-3 py-0.5 text-xs font-bold ${
                  isWinter ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'
                }`}
              >
                {isWinter ? 'Υγρή Περίοδος' : 'Ξηρή Περίοδος'}
              </span>
              {isLocked && (
                <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-0.5 text-xs font-bold text-emerald-800">
                  <Lock className="h-3 w-3" />
                  {offer.revisedAt ? 'Αναθεωρημένη' : 'Οριστική'}
                </span>
              )}
              {/* The nightly job locks a day with whatever draft it holds, so a
                  day can arrive locked without anyone having decided to. */}
              {offer.isZeroBid ? (
                <span
                  className="flex items-center gap-1 rounded-full bg-rose-100 px-3 py-0.5 text-xs font-bold text-rose-800"
                  title="Δεν έγινε υπολογισμός — το σύστημα κλείδωσε μηδενική προσφορά"
                >
                  Μηδενική προσφορά
                </span>
              ) : (
                offer.autoFinalized && (
                  <span
                    className="flex items-center gap-1 rounded-full bg-slate-200 px-3 py-0.5 text-xs font-bold text-slate-700"
                    title="Κλείδωσε από την αυτόματη εργασία των 23:30, χωρίς χρήστη"
                  >
                    Αυτόματη 23:30
                  </span>
                )
              )}
              {offer.hasPendingRevision && (
                <span
                  className="flex items-center gap-1 rounded-full bg-orange-100 px-3 py-0.5 text-xs font-bold text-orange-800"
                  title="Έχει ανέβει νεότερο Excel — εφαρμόζεται με νέα οριστικοποίηση"
                >
                  Εκκρεμεί αναθεώρηση
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-gray-500">
              {isLocked
                ? offer.hasPendingRevision
                  ? 'Οριστική. Οι τιμές που φαίνονται ισχύουν ακόμη — το νέο αρχείο εφαρμόζεται με «Αναθεώρηση Προσφοράς».'
                  : 'Προσοχή: Επεξεργάζεστε οριστικοποιημένη προσφορά. Θα πρέπει να σταλεί ως νέα Αναθεώρηση.'
                : isWinter
                ? 'Επιτρέπεται η λειτουργία ΜΥΗΣ Προεσπέρας / Κάτω Προεσπέρας και Υβριδικών 12ώρων.'
                : 'Ξηρή περίοδος: Οι ΜΥΗΣ είναι εκτός λειτουργίας. Επιτρέπεται αλλαγή ΜΟΝΟ στα Υβριδικά 12ωρα.'}
            </p>
          </div>
        </div>

      </div>

      {/* A day the system couldn't compute: everything below is 0 by design */}
      {offer.isZeroBid && (
        <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 shadow-sm">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-rose-600" />
          <div className="text-sm text-rose-900">
            <p className="font-bold">Δεν έγινε υπολογισμός — μηδενική προσφορά</p>
            <p className="mt-0.5 text-xs text-rose-800">
              Το σύστημα δεν μπόρεσε να υπολογίσει την ημέρα και κλείδωσε προσφορά με 0 σε όλες τις
              ώρες, ώστε να συνεχίσουν οι εργασίες. Για να την αλλάξετε, ορίστε τιμές παρακάτω και
              οριστικοποιήστε ξανά ως αναθεώρηση. Για μελλοντική ημέρα η αναθεώρηση ξανατρέχει τον
              υπολογισμό και απαιτεί το ACROSS.
            </p>
          </div>
        </div>
      )}

      {/* Battery Reserve Banner */}
      <div className="flex items-center gap-3 rounded-2xl bg-indigo-50 p-4 border border-indigo-200 shadow-sm shadow-indigo-900/5">
        <BatteryMedium className="h-6 w-6 text-indigo-600" />
        <div>
          <h5 className="font-bold text-indigo-950">
            Εκτιμώμενο απόθεμα υβριδικής ενέργειας (για {format(parseISO(offer.date), 'dd/MM/yyyy')})
          </h5>
          <p className="text-sm font-semibold text-indigo-700">
            {poolBudget.base.toFixed(2)} MWh
          </p>
        </div>
      </div>

      {/* Hybrid 12h Section */}
      <div className="rounded-3xl border border-indigo-100 bg-gradient-to-br from-indigo-50/50 via-white to-purple-50/30 p-6 shadow-xl shadow-indigo-900/5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="text-lg font-bold text-indigo-950">Υβριδική Μονάδα (1ο & 2ο 12ωρο)</h4>
            <p className="text-xs text-indigo-800/70">
              Βήμα μεταβολής: {limits.hybrid.step} MWh | Ελάχιστο: 0 MWh
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-xl border border-indigo-200 bg-indigo-100/60 px-3 py-1.5 text-xs font-bold text-indigo-900">
              Σύνολο Υβριδικής (Ημέρας): {currentDayHybridTotal} MWh
            </span>
            {allSelectedOffers.length > 1 && (
              <span className="rounded-xl border border-purple-200 bg-purple-100/70 px-3 py-1.5 text-xs font-bold text-purple-950">
                Σύνολο όλων των ημερών: {grandTotalHybridMwh} MWh
              </span>
            )}
          </div>
        </div>

        {isHybridOverLimit && (
          <div className="mb-4 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
            <div className="text-sm text-amber-900">
              <p className="font-bold">
                Προσοχή! Έχετε ξεπεράσει τις εκτιμώμενες δυνατότητες Υβριδικής Προσφοράς.
              </p>
              {offer.hybridWarnings.map((warning) => (
                <p key={warning} className="mt-0.5 text-xs text-amber-800">
                  {warning}
                </p>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* 1st 12h Block */}
          <div className="rounded-2xl border border-indigo-200/80 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-gray-700">1ο 12ωρο (Ώρες 00:00 - 11:59)</span>
              <span className="text-xs font-semibold text-gray-500">
                Μέγιστο Έγχυσης: {maxAllowedInjection1st} MWh
              </span>
            </div>

            <div className="my-4 flex items-center justify-between rounded-xl bg-indigo-50/60 p-4">
              <button
                disabled={!canStepHybrid(0, -1)}
                onClick={() => updateHybridBlock(1, -1)}
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-indigo-700 shadow-sm transition hover:bg-indigo-100 disabled:opacity-40"
                title={`Μείωση κατά ${limits.hybrid.step} MWh (έως 0 MWh)`}
              >
                <Minus className="h-5 w-5" />
              </button>
              <div className="text-center">
                <span className="text-3xl font-extrabold text-indigo-950">
                  {offer.hybrid1st12h.toFixed(2)}
                </span>
                <span className="ml-1.5 text-sm font-semibold text-indigo-600">MWh</span>
              </div>
              <button
                disabled={!canStepHybrid(0, 1)}
                onClick={() => updateHybridBlock(1, 1)}
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-indigo-700 shadow-sm transition hover:bg-indigo-100 disabled:opacity-40"
                title={`Αύξηση κατά ${limits.hybrid.step} MWh`}
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-2 border-t border-indigo-50 pt-3 text-xs">
              {renderProductionCheck(0)}
              {renderPoolCheck()}
            </div>
          </div>

          {/* 2nd 12h Block */}
          <div className="rounded-2xl border border-purple-200/80 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-gray-700">2ο 12ωρο (Ώρες 12:00 - 23:59)</span>
              <span className="text-xs font-semibold text-gray-500">
                Μέγιστο Έγχυσης: {maxAllowedInjection2nd} MWh
              </span>
            </div>

            <div className="my-4 flex items-center justify-between rounded-xl bg-purple-50/60 p-4">
              <button
                disabled={!canStepHybrid(1, -1)}
                onClick={() => updateHybridBlock(2, -1)}
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-purple-700 shadow-sm transition hover:bg-purple-100 disabled:opacity-40"
                title={`Μείωση κατά ${limits.hybrid.step} MWh (έως 0 MWh)`}
              >
                <Minus className="h-5 w-5" />
              </button>
              <div className="text-center">
                <span className="text-3xl font-extrabold text-purple-950">
                  {offer.hybrid2nd12h.toFixed(2)}
                </span>
                <span className="ml-1.5 text-sm font-semibold text-purple-600">MWh</span>
              </div>
              <button
                disabled={!canStepHybrid(1, 1)}
                onClick={() => updateHybridBlock(2, 1)}
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-purple-700 shadow-sm transition hover:bg-purple-100 disabled:opacity-40"
                title={`Αύξηση κατά ${limits.hybrid.step} MWh`}
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-2 border-t border-purple-50 pt-3 text-xs">
              {renderProductionCheck(1)}
              {renderPoolCheck()}
            </div>
          </div>
        </div>

        {allSelectedOffers.length > 1 && (
          <p className="mt-3 text-[11px] text-indigo-800/60">
            Το απόθεμα ενδέχεται να αυξηθεί από μελλοντικές αντλήσεις (pump).
          </p>
        )}
      </div>

      {/* ΜΥΗΣ Προεσπέρας & Κάτω Προεσπέρας Hourly Adjustments */}
      <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-xl shadow-gray-900/5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-4">
          <div>
            <h4 className="text-lg font-bold text-gray-900">
              Επεξεργασία Προσφοράς ΜΥΗΣ Προεσπέρας και Κάτω Προεσπέρας
            </h4>
            <p className="text-xs text-gray-500">
              ΜΥΗΣ Π.: [{limits.upper.min} - {limits.upper.max} MW] | ΜΥΗΣ Κάτω Π.: [{limits.lower.min} -{' '}
              {limits.lower.max} MW]
            </p>
          </div>


        </div>

        {/* 24-Hour Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-gray-700">
                <th className="p-2.5 font-bold">Ώρα</th>
                <th className="p-2.5 font-bold text-blue-700">
                  ΜΥΗΣ Π. (MW)
                  <span className="ml-1 text-[10px] font-normal text-gray-500">
                    ({limits.upper.min} - {limits.upper.max})
                  </span>
                </th>
                <th className="p-2.5 font-bold text-emerald-700">
                  ΜΥΗΣ Κάτω Π. (MW)
                  <span className="ml-1 text-[10px] font-normal text-gray-500">
                    ({limits.lower.min} - {limits.lower.max})
                  </span>
                </th>
                <th className="p-2.5 font-bold text-gray-900">
                  Σύνολο Ώρας (MW)
                  <span className="ml-1 text-[10px] font-normal text-gray-500">(ΜΥΗΣ Π. + Κάτω Π.)</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {offer.hourly.map((h) => {
                const totalHourMw = Number((h.mysAno + h.mysKato).toFixed(1));
                const anoComputed = Number(offer.computed.mysAno?.[h.hour] ?? 0);
                const katoComputed = Number(offer.computed.mysKato?.[h.hour] ?? 0);

                return (
                  <tr key={h.hour} className="border-b border-gray-100 hover:bg-blue-50/30">
                    <td className="p-2.5 font-semibold text-gray-700">
                      {h.hour.toString().padStart(2, '0')}:00
                    </td>

                    {/* ΜΥΗΣ Άνω Προεσπέρας */}
                    <td className="p-2.5">
                      <div className="flex items-center gap-1.5">
                        <button
                          disabled={!canEditHydro || !canStep(h.mysAno, anoComputed, -1, limits.upper)}
                          onClick={() => updateHourlyValue(h.hour, 'mysAno', -1)}
                          className="flex h-6 w-6 items-center justify-center rounded border border-gray-300 bg-white text-gray-700 shadow-sm transition hover:bg-gray-100 disabled:opacity-30"
                        >
                          -
                        </button>
                        <span className="w-14 text-center font-bold text-blue-900">
                          {h.mysAno.toFixed(1)}
                        </span>
                        <button
                          disabled={!canEditHydro || !canStep(h.mysAno, anoComputed, 1, limits.upper)}
                          onClick={() => updateHourlyValue(h.hour, 'mysAno', 1)}
                          className="flex h-6 w-6 items-center justify-center rounded border border-gray-300 bg-white text-gray-700 shadow-sm transition hover:bg-gray-100 disabled:opacity-30"
                        >
                          +
                        </button>
                      </div>
                    </td>

                    {/* ΜΥΗΣ Κάτω Προεσπέρας */}
                    <td className="p-2.5">
                      <div className="flex items-center gap-1.5">
                        <button
                          disabled={!canEditHydro || !canStep(h.mysKato, katoComputed, -1, limits.lower)}
                          onClick={() => updateHourlyValue(h.hour, 'mysKato', -1)}
                          className="flex h-6 w-6 items-center justify-center rounded border border-gray-300 bg-white text-gray-700 shadow-sm transition hover:bg-gray-100 disabled:opacity-30"
                        >
                          -
                        </button>
                        <span className="w-14 text-center font-bold text-emerald-900">
                          {h.mysKato.toFixed(1)}
                        </span>
                        <button
                          disabled={!canEditHydro || !canStep(h.mysKato, katoComputed, 1, limits.lower)}
                          onClick={() => updateHourlyValue(h.hour, 'mysKato', 1)}
                          className="flex h-6 w-6 items-center justify-center rounded border border-gray-300 bg-white text-gray-700 shadow-sm transition hover:bg-gray-100 disabled:opacity-30"
                        >
                          +
                        </button>
                      </div>
                    </td>

                    <td className="p-2.5 font-bold text-gray-900">{totalHourMw} MW</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <ConfirmationDialog
        isOpen={showConfirm}
        title="Επεξεργασία Οριστικοποιημένης Προσφοράς"
        message="Η προσφορά έχει ήδη οριστικοποιηθεί. Θέλετε σίγουρα να την επεξεργαστείτε; Κάθε αλλαγή θα δρομολογηθεί ως νέα Αναθεώρηση."
        confirmText="Συνέχεια (Επεξεργασία)"
        cancelText="Ακύρωση"
        type="warning"
        onConfirm={() => {
          setHasConfirmedEdit(true);
          setShowConfirm(false);
          if (pendingAction) {
            pendingAction();
            setPendingAction(null);
          }
        }}
        onCancel={() => {
          setShowConfirm(false);
          setPendingAction(null);
        }}
      />
    </div>
  );
};

export default OfferAdjuster;
